/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/invoiceUploadSettings.scss';

import {AwesomeIcon, ChangeFunction, SelectItemShape} from '@dvrd/dvr-controls';

import React, {Fragment, MouseEventHandler, useContext, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {ActivateHandle, CustomerType, InvoiceRuleType, InvoiceUploadRule} from '../../util/interfaces';

import {BolmateButton, BolmateInput, BolmateNumberInput, BolmateSelect} from '../controls';

import {getCustomerTypeItems} from '../../util/emailStepUtil';

import StepCountriesController from '../controls/country/select/countrySelect/countrySelectController';

import classNames from 'classnames';

import BolmateSwitch from '../controls/switch/bolmateSwitch';

import InfoIcon from '../icon/infoIcon';

import {AppContext} from '../misc/appContext';

interface Props {
    onAddRule: MouseEventHandler;
    onUpdateRule: (rule: InvoiceUploadRule, callback?: (success: boolean) => void) => void;
    onDeleteRule: (rule: InvoiceUploadRule) => MouseEventHandler;
    onSubmit: MouseEventHandler;
    onChangeProactiveUpload: ChangeFunction<boolean>;
    onChangeAutoRespond: ChangeFunction<boolean>;
    onChangeAutoRespondOld: ChangeFunction<boolean>;
    onChangeOldNumPrefix: ChangeFunction<string>;
    onChangeOldNumStart: ChangeFunction<string>;
    onChangeNotification: ChangeFunction<boolean>;
    onChangeNotifyMail: ChangeFunction<string>;
    notifyMail: string;
    loading: boolean;
    rules: Array<InvoiceUploadRule>;
    hasChanged: boolean;
    respondOldEnabled: boolean;
    proactiveEnabled: boolean;
    respondEnabled: boolean;
    notificationEnabled: boolean;
    oldNumPrefix: string;
    oldNumStart: string;
    noSubTitle?: boolean;
}

export default function InvoiceUploadSettings(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {
        loading,
        rules,
        onAddRule,
        onDeleteRule,
        onSubmit,
        onUpdateRule,
        hasChanged,
        onChangeProactiveUpload,
        onChangeAutoRespond,
        onChangeNotifyMail,
        notifyMail,
        onChangeNotification,
        onChangeAutoRespondOld,
        onChangeOldNumStart,
        oldNumPrefix,
        onChangeOldNumPrefix,
        respondOldEnabled,
        oldNumStart,
        notificationEnabled,
        respondEnabled,
        proactiveEnabled,
        noSubTitle
    } = props;

    function renderInfo() {
        return (
            <div className='info-container'>
                <InfoIcon className='info-icon' />
                <p className='info-message'>
                    <b>{t('invoiceUploadSettings.proactiveUpload', 'Proactief uploaden')}</b>
                    <br />
                    {t(
                        'invoiceUploadSettings.proactiveInfo',
                        'Met proactief uploaden zorg je ervoor dat voor elke nieuwe order, de factuur na voltooiing van order wordt geüpload. Klanten kunnen de btw-factuur dan vinden en downloaden in hun Bol klantportaal. Zo voorkom je factuuraanvragen.'
                    )}
                    <br />
                    <br />
                    <b>{t('invoiceUploadSettings.autoRespondTitle', 'Automatisch aanvragen beantwoorden')}</b>
                    <br />
                    {t(
                        'invoiceUploadSettings.autoRespondInfo',
                        'Indien de factuur van een order nog niet is geüpload, kan een klant de factuur aanvragen. Met deze optie wordt er automatisch gereageerd op deze aanvraag met de bijbehorende factuur. Door ook de optie proactief uploaden aan te vinken, zorg je ervoor dat er steeds minder van dit soort aanvragen zijn, omdat de factuur al klaar zal staan en men deze niet meer aanvraagt.'
                    )}
                    <br />
                    <br />
                    <b>{t('invoiceUploadSettings.recommended', 'Aanbevolen')}</b>
                    <br />
                    {t(
                        'invoiceUploadSettings.recommendedInfo',
                        'Wij raden aan zowel proactief uploaden als automatisch beantwoorden aan te vinken.'
                    )}
                </p>
            </div>
        );
    }

    function renderToggles() {
        return (
            <div className='invoice-upload-setting toggles'>
                {noSubTitle ? (
                    <div className='with-info'>
                        <label className='tab-title'>
                            {t('invoiceUploadSettings.autoUpload', 'Automatisch uploaden')}
                        </label>
                        {renderInfo()}
                    </div>
                ) : (
                    <label className='tab-title'>{t('invoiceUploadSettings.autoUpload', 'Automatisch uploaden')}</label>
                )}
                {!noSubTitle && (
                    <div className='with-info'>
                        <p className='description'>
                            {t('invoiceUploadSettings.sameAsTopRight', 'Dezelfde instellingen als rechtsboven')}
                        </p>
                        {renderInfo()}
                    </div>
                )}
                <div className='toggles-container'>
                    <div className='auto-toggle'>
                        <BolmateSwitch value={proactiveEnabled} onChange={onChangeProactiveUpload} activeGreen />
                        <label>{t('invoiceUploadSettings.proactiveUpload', 'Proactief uploaden')}</label>
                    </div>
                    <div className='auto-toggle'>
                        <BolmateSwitch value={respondEnabled} onChange={onChangeAutoRespond} activeGreen />
                        <label>{t('invoiceUploadSettings.autoRespond', 'Auto aanvragen beantwoorden')}</label>
                    </div>
                    {renderAutoRespondOld()}
                </div>
            </div>
        );
    }

    function renderAutoRespondOld() {
        return (
            <div className='respond-old-container'>
                <div className='auto-toggle'>
                    <BolmateSwitch value={respondOldEnabled} onChange={onChangeAutoRespondOld} activeGreen />
                    <label>
                        {t(
                            'invoiceUploadSettings.oldOrders.toggleLabel',
                            'Auto aanvragen beantwoorden voor oude orders'
                        )}
                    </label>
                    <div className='info-container'>
                        <InfoIcon className='info-icon' />
                        <p className='info-message'>
                            <b>{t('invoiceUploadSettings.oldOrders.infoTitle', 'Factuuraanvragen voor oude orders')}</b>
                            <br />
                            {t(
                                'invoiceUploadSettings.oldOrders.infoLine1',
                                'Jouw klanten kunnen voor orders tot 2 jaar terug een factuur aanvragen. Het is mogelijk dat wij deze order niet in het systeem hebben, omdat jouw Bolmate account toen nog niet bestond.'
                            )}
                            <br />
                            <br />
                            {t(
                                'invoiceUploadSettings.oldOrders.infoLine2',
                                'We kunnen deze factuuraanvraag wel automatisch beantwoorden met een factuur upload, maar doordat de Belastingdienst vereist dat jouw factuurnummering chronologisch oploopt, kunnen we hiervoor niet het standaard factuurnummer gebruiken.'
                            )}
                            <br />
                            <br />
                            {t(
                                'invoiceUploadSettings.oldOrders.infoLine3',
                                'Je kunt daarom een andere factuurnummerreeks kiezen voor de facturen voor oude orders, op deze manier blijven de nummers chronologisch oplopend.'
                            )}
                        </p>
                    </div>
                </div>
                {respondOldEnabled && (
                    <div className='old-invoice-num-settings'>
                        <div className='row sm'>
                            <label>
                                {t('invoiceUploadSettings.oldOrders.invoiceNumTitle', 'Factuurnummer voor oude orders')}
                            </label>
                        </div>
                        <div className='row'>
                            <BolmateInput
                                value={oldNumPrefix}
                                onChange={onChangeOldNumPrefix}
                                label={t('invoiceUploadSettings.oldOrders.prefix', 'Voorvoegsel')}
                            />
                            <BolmateNumberInput
                                value={oldNumStart}
                                onChange={onChangeOldNumStart}
                                label={t('invoiceUploadSettings.oldOrders.startWith', 'Starten met')}
                                wholeNumbers
                                disableSymbols
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    function renderRules() {
        return (
            <div className='invoice-upload-setting'>
                <label className='tab-title'>
                    {t('invoiceUploadSettings.rules.title', 'Alleen uploaden bij bepaalde voorwaarde')}
                </label>
                <p className='description'>
                    {t(
                        'invoiceUploadSettings.rules.description',
                        'Facturen worden alleen automatisch geüpload als aan deze voorwaarde wordt voldaan.'
                    )}
                </p>
                <div className='invoice-upload-body'>
                    <div className='rules'>
                        {rules.map((rule: InvoiceUploadRule, index: number) => (
                            <Rule
                                onUpdateRule={onUpdateRule}
                                onDeleteRule={onDeleteRule}
                                rule={rule}
                                index={index}
                                loading={loading}
                            />
                        ))}
                    </div>
                </div>
                <div className='btn-container btn-2'>
                    <BolmateButton
                        onClick={onAddRule}
                        label={t('invoiceUploadSettings.rules.addRule', 'Regel toevoegen')}
                        disabled={loading}
                        secondary
                    />
                    <BolmateButton
                        onClick={onSubmit}
                        label={t('invoiceUploadSettings.save', 'Opslaan')}
                        disabled={loading || !hasChanged}
                    />
                </div>
            </div>
        );
    }

    function renderNotification() {
        return (
            <div className='invoice-upload-setting'>
                <label className='tab-title'>
                    {t('invoiceUploadSettings.notify.title', 'Notificatie e-mail bij vereiste handmatige actie')}
                </label>
                <p className='description'>
                    {t(
                        'invoiceUploadSettings.notify.desc1',
                        'Stuur mij een e-mail notificatie als een nieuwe factuuraanvraag niet automatisch beantwoord kan worden.'
                    )}
                    <br />
                    {t(
                        'invoiceUploadSettings.notify.desc2',
                        'Bijvoorbeeld omdat de factuur niet in de Bolmate database zit, als de order van vóór begin gebruik Bolmate is.'
                    )}
                </p>
                <div className='notification-container'>
                    <label>{t('invoiceUploadSettings.notify.toggle', 'Stuur mij een e-mail notificatie')}</label>
                    <BolmateSwitch value={notificationEnabled} onChange={onChangeNotification} activeGreen />
                    {notificationEnabled && (
                        <BolmateInput
                            onChange={onChangeNotifyMail}
                            value={notifyMail}
                            disabled={loading}
                            inputProps={{type: 'email', placeholder: user?.email}}
                            label={t('invoiceUploadSettings.notify.emailLabel', 'Notificatie e-mailadres')}
                            className='email-field'
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='invoice-upload-settings'>
            {renderToggles()}
            {renderRules()}
            {renderNotification()}
            <div className='btn-container'>
                <BolmateButton
                    onClick={onSubmit}
                    label={t('invoiceUploadSettings.save', 'Opslaan')}
                    disabled={loading || !hasChanged}
                />
            </div>
        </div>
    );
}

interface RuleProps {
    onUpdateRule: (rule: InvoiceUploadRule) => void;
    onDeleteRule: (rule: InvoiceUploadRule) => MouseEventHandler;
    rule: InvoiceUploadRule;
    index: number;
    loading: boolean;
}

function Rule(props: RuleProps) {
    const {t} = useTranslation();
    const typeItems: SelectItemShape[] = [
        {label: t('invoiceUploadSettings.ruleType.only', 'Alleen'), value: InvoiceRuleType.WHITELIST},
        {label: t('invoiceUploadSettings.ruleType.not', 'Niet'), value: InvoiceRuleType.BLACKLIST}
    ];
    const customerTypeItems = getCustomerTypeItems();
    const {onUpdateRule, rule, index, loading, onDeleteRule} = props;
    const countryRef = useRef<ActivateHandle>(null);

    function onClickCountries() {
        countryRef.current?.activate(rule.countries ?? []);
    }

    function onChangeRuleType(ruleType: InvoiceRuleType) {
        rule.rule_type = ruleType;
        onUpdateRule(rule);
    }

    function onChangeCustomerType(customerType: CustomerType) {
        rule.customer_type = customerType;
        onUpdateRule(rule);
    }

    function onChangeCountries(countries: Set<string>, allSelected: boolean, callback: (close: boolean) => void) {
        // const countryCodes = Array.from(countries).map((country: string) => getCountryCode(country));
        if (allSelected) rule.countries = [];
        else rule.countries = Array.from(countries);
        onUpdateRule(rule);
        callback(true);
    }

    function renderCountry() {
        const count = rule.countries?.length ?? 0;
        let label: string;
        if (!count) label = t('invoiceUploadSettings.rule.allCountries', 'Alle landen');
        else if (count === 1) label = rule.countries![0];
        else label = `${count} ${t('invoiceUploadSettings.rule.countries', 'landen')}`;
        return (
            <section>
                <label className='fixed-text'>{t('invoiceUploadSettings.rule.in', 'in')}</label>
                {renderVarButton(label, onClickCountries)}
            </section>
        );
    }

    function renderVarButton(label: string, onClick: MouseEventHandler) {
        const clickable = !loading;
        return (
            <div
                className={classNames('var-button', !clickable && 'disabled')}
                onClick={clickable ? onClick : undefined}
            >
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    return (
        <Fragment key={index}>
            <div className='upload-rule'>
                <section>
                    <BolmateSelect
                        value={rule.rule_type}
                        onChange={onChangeRuleType}
                        items={typeItems}
                        className='select-field'
                        itemsPosition='top'
                    />
                </section>
                <section>
                    <label className='fixed-text'>
                        {t('invoiceUploadSettings.rule.autoUploadFor', 'automatisch uploaden voor')}
                    </label>
                    <BolmateSelect
                        onChange={onChangeCustomerType}
                        value={rule.customer_type}
                        items={customerTypeItems}
                        className='select-field'
                        itemsPosition='top'
                    />
                </section>
                {renderCountry()}
                <AwesomeIcon name='trash-alt' className='btn-delete' onClick={onDeleteRule(rule)} />
            </div>
            <StepCountriesController ref={countryRef} loading={loading} onSubmit={onChangeCountries} />
        </Fragment>
    );
}
